/* lib/fcScale.js — шкала food cost активного пространства (#502). Зеркало backend
   analytics/fc_scale.py: три границы делят FC на «в норме» / «выше цели» / «критично» / «срочно».
   Значение ставит AuthProvider при выборе пространства (поле `fc_scale` пространства из
   /api/auth/me, как валюту); бейджи «Продукции», ABC и Аналитика читают его отсюда. Сохранённый
   отчёт показывает СВОЮ шкалу (`result.fc_scale`) — передаётся параметром. */

import React from 'react';

export const DEFAULT_FC_SCALE = Object.freeze({ good: 20, above_target: 25, critical: 30 });

let current = DEFAULT_FC_SCALE;
const listeners = new Set();

/** Шкала из ответа сервера: три числа по возрастанию, иначе — умолчание. */
export function normalizeFcScale(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_FC_SCALE;
  const good = Number(raw.good);
  const above = Number(raw.above_target);
  const critical = Number(raw.critical);
  if (![good, above, critical].every(Number.isFinite)) return DEFAULT_FC_SCALE;
  if (!(good > 0 && good < above && above < critical && critical <= 100)) return DEFAULT_FC_SCALE;
  return { good, above_target: above, critical };
}

export function getFcScale() { return current; }

let notified = current;

/** Значение без оповещения — в рендере провайдера (дети читают уже новую шкалу). */
export function primeFcScale(raw) {
  current = normalizeFcScale(raw);
}

/** Значение + оповещение подписчиков — после коммита (эффект) или из обработчика. */
export function setFcScale(raw) {
  const next = normalizeFcScale(raw);
  current = next;
  if (next.good === notified.good && next.above_target === notified.above_target
      && next.critical === notified.critical) return;
  notified = next;
  listeners.forEach((fn) => fn());
}

function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/** Хук: компонент перерисуется, когда шкала пространства сменится. */
export function useFcScale() {
  return React.useSyncExternalStore(subscribe, getFcScale, getFcScale);
}

/** Оценка FC по шкале: no_data | good | above_target | critical | urgent. */
export function fcStatusFor(pct, scale = current) {
  const s = scale || DEFAULT_FC_SCALE;
  if (pct == null || pct === '' || !(Number(pct) > 0)) return 'no_data';
  const v = Number(pct);
  if (v < s.good) return 'good';
  if (v < s.above_target) return 'above_target';
  if (v < s.critical) return 'critical';
  return 'urgent';
}

/** Цвет бейджа: норма — ok, выше цели — warn, критично и срочно — bad. */
export const FC_STATUS_TONE = { no_data: 'neutral', good: 'ok', above_target: 'warn', critical: 'bad', urgent: 'bad' };
export const FC_STATUS_VERDICT = {
  good: 'в норме', above_target: 'выше цели', critical: 'критично', urgent: 'срочно',
};

const num = (v) => String(v).replace('.', ',');

/** «шкала: < 20 норма · 20–25 выше цели · 25–30 критично · ≥ 30 срочно» — подсказка отчёта. */
export function fcScaleHint(scale = current) {
  const s = scale || DEFAULT_FC_SCALE;
  return `шкала: <${num(s.good)} норма · ${num(s.good)}–${num(s.above_target)} выше цели · `
    + `${num(s.above_target)}–${num(s.critical)} критично · ≥${num(s.critical)} срочно`;
}

/** «20–25 %» — границы класса словами (подписи ABC и вкладки настроек). */
export function fcRangeText(status, scale = current) {
  const s = scale || DEFAULT_FC_SCALE;
  switch (status) {
    case 'good': return `до ${num(s.good)} %`;
    case 'above_target': return `${num(s.good)}–${num(s.above_target)} %`;
    case 'critical': return `${num(s.above_target)}–${num(s.critical)} %`;
    case 'urgent': return `от ${num(s.critical)} %`;
    default: return '';
  }
}
